/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

const FIRST_PARTY_DIST = '\\/dist\\/'

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeOrigin(hostOrUrl: string): string | null {
  const trimmed = hostOrUrl.trim().replace(/\/$/, '')
  if (!trimmed) return null
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return new URL(trimmed).origin
    }
    return new URL(`https://${trimmed}`).origin
  } catch {
    return null
  }
}

function remoteLaunchUrls(remotes: unknown): string[] {
  if (!remotes || typeof remotes !== 'object') return []
  return Object.values(remotes as Record<string, unknown>).flatMap(value => {
    if (typeof value === 'string') return [value]
    const launchUrl = (value as {launch_url?: unknown} | null)?.launch_url
    return typeof launchUrl === 'string' ? [launchUrl] : []
  })
}

export function buildSentryAllowUrls(options: {
  assetHost?: string | null
  pageOrigin?: string | null
  remotes?: unknown
}): RegExp[] {
  const patterns: RegExp[] = []
  const seen = new Set<string>()

  const addOrigin = (raw: string | null | undefined, pathPrefix: string) => {
    const origin = raw ? normalizeOrigin(raw) : null
    if (!origin || seen.has(origin)) return
    seen.add(origin)
    patterns.push(new RegExp(`^${escapeRegExp(origin)}${pathPrefix}`, 'i'))
  }

  for (const launchUrl of remoteLaunchUrls(options.remotes)) {
    addOrigin(launchUrl, '\\/')
  }
  addOrigin(options.assetHost, FIRST_PARTY_DIST)
  addOrigin(options.pageOrigin, FIRST_PARTY_DIST)

  // Some events expose path-only frames before the SDK absolutizes them.
  patterns.push(new RegExp(`^${FIRST_PARTY_DIST}`, 'i'))

  return patterns
}
